import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, FormControl } from '@angular/forms';
import {
  SharedModule,
  StringCustomFieldConfig,
  DynamicFormInputComponent,
  DataService,
  NotificationService
} from '@vendure/admin-ui/core';
import { ID, ProductVariant } from '@vendure/core';
import { forkJoin, Observable, of, take } from 'rxjs';
import { DEFAULT_ACTIVE_LANGUAGE } from '../../constants';
import {
  MarketingBannerData,
  MarketingBannerType,
  MobileMediaPosition,
  defaultMarketingBannerData,
  TextAlignment,
  ButtonPositionType,
  defaultBannerButtonData,
  BannerButtonData,
  NavContext,
} from '../../ui-types';
import { clampUnitInterval, normalizeShadowTransparency, safeJSONParse } from '../../utils';
import { Asset, ProductReview } from '../../generated';

interface GetProductReviewQuery {
  productReview: ProductReview;
}

@Component({
  selector: 'vdr-marketing-banner-input',
  templateUrl: './marketing-banner-form.component.html',
  styleUrls: ['../../styles.scss'],
  standalone: true,
  imports: [SharedModule],
})
export class MarketingBannerInputComponent implements OnInit, AfterViewInit {
  @ViewChild('mediasInput', { static: false })
  set mediasInputSetter(comp: DynamicFormInputComponent | undefined) {
    this.mediasInputInstance = comp ?? null;
    if (comp) this.attachMediasInput(comp);
  }

  @ViewChild('mobileMediasInput', { static: false })
  set mobileMediasInputSetter(comp: DynamicFormInputComponent | undefined) {
    this.mobileMediasInputInstance = comp ?? null;
    if (comp) this.attachMobileMediasInput(comp);
  }

  private mediasInputInstance: DynamicFormInputComponent | null = null;
  private mobileMediasInputInstance: DynamicFormInputComponent | null = null;

  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  bannerTypeControl = new FormControl();
  mediaHeightControl = new FormControl();
  mediaRoundedCornersControl = new FormControl();
  mediasControl = new UntypedFormControl();
  mobileMediasControl = new UntypedFormControl();
  mobileMediaPositionControl = new FormControl();
  titleControl = new FormControl();
  textControl = new FormControl();
  titleFontSizeControl = new FormControl();
  textFontSizeControl  = new FormControl();
  textBarStyleControl = new FormControl();
  leftRightPartRatioControl = new FormControl();
  mediaTextGapControl = new FormControl();
  overlayOpacityControl = new FormControl();
  backgroundMediaControl = new FormControl();
  backgroundMobileMediaControl = new FormControl();
  backgroundColorControl = new FormControl();
  textColorControl = new FormControl();
  textAlignmentControl = new FormControl();
  textWidthControl = new FormControl();
  titleIndentationControl = new FormControl();
  textIndentationControl = new FormControl();
  buttonControl = new UntypedFormControl();
  buttonPositionControl = new FormControl();
  buttonAlignmentControl = new FormControl();
  buttonLocationXControl = new FormControl();
  buttonLocationYControl = new FormControl();
  buttonPositionMobileControl = new FormControl();
  buttonAlignmentMobileControl = new FormControl();
  buttonLocationXMobileControl = new FormControl();
  buttonLocationYMobileControl = new FormControl();

  mobileMediaStyleControl = new FormControl();
  layoutStyleControl = new FormControl();
  autoSlideDelayControl = new FormControl();

  secColTitleControl = new FormControl();
  secColTextControl = new FormControl();
  secColTitleFontSizeControl = new FormControl();
  secColTextFontSizeControl  = new FormControl();
  secColTextAlignmentControl = new FormControl();
  secColTextWidthControl = new FormControl();
  secColTitleIndentationControl = new FormControl();
  secColTextIndentationControl = new FormControl();

  marketingBannerData: MarketingBannerData;

  partRatioLabel: string;

  private activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
  private languageChanges$?: Observable<unknown>;
  private navContext?: NavContext;
  buttonInputDef: any;
  titleTextValueDef: any;
  textTextValueDef: any;
  secColTitleTextValueDef: any;
  secColTextTextValueDef: any;


  constructor(
    private dataService: DataService,
    private changeDetector: ChangeDetectorRef,
    private notificationService: NotificationService
  ) {}

  async ngOnInit() {
    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) this.activeLanguageCode = args.activeLanguageCode;
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    this.navContext = (this.config?.ui?.options as any)?.navContext as NavContext | undefined;
    this.buttonInputDef = {
      type: 'string',
      ui: {
        component: 'banner-button-form',
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
        options: {
          navContext: this.navContext,
        },
      },
    };
    this.titleTextValueDef = this.createTextValueDef();
    this.textTextValueDef = this.createTextValueDef();
    this.secColTitleTextValueDef = this.createTextValueDef();
    this.secColTextTextValueDef = this.createTextValueDef();
    this.marketingBannerData = safeJSONParse<MarketingBannerData>(
      this.formControl.value,
      this.notificationService,
      defaultMarketingBannerData,
    )!;
    this.initializeFormControls();

    // Load assets based on IDs
    if (this.marketingBannerData && this.marketingBannerData?.mediaIds?.length > 0) {
      this.loadAssetsByIds(this.marketingBannerData.mediaIds).subscribe(assets => {
        this.mediasControl.setValue(assets);
        if (this.mediasInputInstance) this.mediasInputInstance.writeValue(assets);
      });
    } else {
      this.mediasControl.setValue([]);
    }

    if (this.marketingBannerData && this.marketingBannerData?.mobileMediaIds?.length > 0) {
      this.loadAssetsByIds(this.marketingBannerData.mobileMediaIds).subscribe((assets) => {
        this.mobileMediasControl.setValue(assets);
        if (this.mobileMediasInputInstance) this.mobileMediasInputInstance.writeValue(assets);
      });
    } else {
      this.mobileMediasControl.setValue([]);
    }

    if (this.marketingBannerData?.backgroundMediaId) {
      this.dataService.product
        .getAsset(this.marketingBannerData.backgroundMediaId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.backgroundMediaControl.setValue(asset);
        });
    }

    if (this.marketingBannerData?.backgroundMobileMediaId) {
      this.dataService.product
        .getAsset(this.marketingBannerData.backgroundMobileMediaId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.backgroundMobileMediaControl.setValue(asset);
        });
    }

    if (this.marketingBannerData?.type === 'middle-media-sides-text') {
      this.partRatioLabel = 'Middle Media Part Ratio';
    } else {
      this.partRatioLabel = 'Left Right Part Ratio';
    }
  }

  async ngAfterViewInit() {
    // Subscribe to changes for existing fields
    this.bannerTypeControl.valueChanges.subscribe(this.onTypeChange);
    this.mediaHeightControl.valueChanges.subscribe(this.onMediaHeightChange);
    this.mediaRoundedCornersControl.valueChanges.subscribe(this.onMediaRoundedCornersChange);
    this.titleControl.valueChanges.subscribe(this.onTitleChange);
    this.textControl.valueChanges.subscribe(this.onTextChange);
    this.titleFontSizeControl.valueChanges.subscribe(this.onTitleFontSizeChange);
    this.textFontSizeControl.valueChanges.subscribe(this.onTextFontSizeChange);
    this.textBarStyleControl.valueChanges.subscribe(this.onTextBarStyleChange);
    this.leftRightPartRatioControl.valueChanges.subscribe(this.onLeftRightPartRatioChange);
    this.mediaTextGapControl.valueChanges.subscribe(this.onMediaTextGapChange);
    this.overlayOpacityControl.valueChanges.subscribe(this.onOverlayOpacityChange);
    this.backgroundMediaControl.valueChanges.subscribe(this.onBackgroundMediaChange);
    this.backgroundMobileMediaControl.valueChanges.subscribe(this.onBackgroundMobileMediaChange);
    this.backgroundColorControl.valueChanges.subscribe(this.onBackgroundColorChange);
    this.textColorControl.valueChanges.subscribe(this.onTextColorChange);
    this.textAlignmentControl.valueChanges.subscribe(this.onTextAlignmentChange);
    this.textWidthControl.valueChanges.subscribe(this.onTextWidthChange);
    this.titleIndentationControl.valueChanges.subscribe(this.onTitleIndentationChange);
    this.textIndentationControl.valueChanges.subscribe(this.onTextIndentationChange);
    this.secColTitleControl.valueChanges.subscribe(this.onSecColTitleChange);
    this.secColTextControl.valueChanges.subscribe(this.onSecColTextChange);
    this.secColTitleFontSizeControl.valueChanges.subscribe(this.onSecColTitleFontSizeChange);
    this.secColTextFontSizeControl.valueChanges.subscribe(this.onSecColTextFontSizeChange);
    this.secColTextAlignmentControl.valueChanges.subscribe(this.onSecColTextAlignmentChange);
    this.secColTextWidthControl.valueChanges.subscribe(this.onSecColTextWidthChange);
    this.secColTitleIndentationControl.valueChanges.subscribe(this.onSecColTitleIndentationChange);
    this.secColTextIndentationControl.valueChanges.subscribe(this.onSecColTextIndentationChange);
    this.buttonControl.valueChanges.subscribe(this.onButtonChange);
    this.mobileMediaPositionControl.valueChanges.subscribe(this.onMobileMediaPositionChange);
    this.buttonPositionControl.valueChanges.subscribe(this.onButtonPositionChange);
    this.buttonAlignmentControl.valueChanges.subscribe(this.onButtonAlignmentChange);
    this.buttonLocationXControl.valueChanges.subscribe(this.onButtonLocationXChange);
    this.buttonLocationYControl.valueChanges.subscribe(this.onButtonLocationYChange);
    this.buttonPositionMobileControl.valueChanges.subscribe(this.onButtonPositionMobileChange);
    this.buttonAlignmentMobileControl.valueChanges.subscribe(this.onButtonAlignmentMobileChange);
    this.buttonLocationXMobileControl.valueChanges.subscribe(this.onButtonLocationXMobileChange);
    this.buttonLocationYMobileControl.valueChanges.subscribe(this.onButtonLocationYMobileChange);

    this.mobileMediaStyleControl.valueChanges.subscribe(this.onMobileMediaStyleChange);
    this.layoutStyleControl.valueChanges.subscribe(this.onLayoutStyleChange);
    this.autoSlideDelayControl.valueChanges.subscribe(this.onautoSlideDelayChange);
    this.buttonPositionMobileControl.setValue(this.marketingBannerData?.buttonPositionMobile);
    this.buttonPositionControl.setValue(this.marketingBannerData?.buttonPosition);
    this.changeDetector.markForCheck();
  }

  private createTextValueDef(): any {
    return {
      type: 'string',
      ui: {
        component: 'text-value-input',
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
      },
    };
  }

  private initializeFormControls() {
    this.bannerTypeControl.setValue(this.marketingBannerData?.type);
    this.mediaHeightControl.setValue(this.marketingBannerData?.mediaHeight || 400);
    this.mediaRoundedCornersControl.setValue(this.marketingBannerData?.mediaRoundedCorners || 'none');
    this.mobileMediaPositionControl.setValue(this.marketingBannerData?.mobileMediaPosition);
    this.titleControl.setValue(this.toTextValueJson(this.marketingBannerData?.title));
    this.textControl.setValue(this.toTextValueJson(this.marketingBannerData?.text));
    this.titleFontSizeControl.setValue(this.marketingBannerData?.titleFontSize ?? 32);
    this.textFontSizeControl.setValue(this.marketingBannerData?.textFontSize ?? 16);
    this.textBarStyleControl.setValue(this.marketingBannerData?.textBarStyle || 'none');
    this.leftRightPartRatioControl.setValue(this.marketingBannerData?.leftRightPartRatio);
    this.mediaTextGapControl.setValue(this.marketingBannerData?.mediaTextGap || 0);
    const shadowTransparency = normalizeShadowTransparency(this.marketingBannerData?.shadowTransparency);
    if (this.marketingBannerData) {
      this.marketingBannerData.shadowTransparency = shadowTransparency;
    }
    this.overlayOpacityControl.setValue(1.0 - shadowTransparency);
    this.backgroundColorControl.setValue(this.marketingBannerData?.backgroundColor);
    this.textColorControl.setValue(this.marketingBannerData?.textColor);
    this.textAlignmentControl.setValue(this.marketingBannerData?.textAlignment);
    this.textWidthControl.setValue(this.marketingBannerData?.textWidth || 1);
    this.titleIndentationControl.setValue(this.marketingBannerData?.titleIndentation || 0);
    this.textIndentationControl.setValue(this.marketingBannerData?.textIndentation || 0);
    this.secColTitleControl.setValue(this.toTextValueJson(this.marketingBannerData?.secColTitle));
    this.secColTextControl.setValue(this.toTextValueJson(this.marketingBannerData?.secColText));
    this.secColTitleFontSizeControl.setValue(
    this.marketingBannerData?.secColTitleFontSize ?? 32);
    this.secColTextFontSizeControl.setValue(
    this.marketingBannerData?.secColTextFontSize ?? 16);
    this.secColTextAlignmentControl.setValue(
    this.marketingBannerData?.secColTextAlignment ?? TextAlignment.Left);
    this.secColTextWidthControl.setValue(
    this.marketingBannerData?.secColTextWidth ?? 1);
    this.secColTitleIndentationControl.setValue(
    this.marketingBannerData?.secColTitleIndentation ?? 0);
    this.secColTextIndentationControl.setValue(
    this.marketingBannerData?.secColTextIndentation ?? 0);
    this.buttonControl.setValue(JSON.stringify(this.marketingBannerData?.button));
    this.buttonAlignmentControl.setValue(this.marketingBannerData?.buttonAlignment ?? TextAlignment.Center);
    this.buttonPositionControl.setValue(this.marketingBannerData?.buttonPosition);
    this.buttonLocationXControl.setValue(this.marketingBannerData?.buttonLocationX);
    this.buttonLocationYControl.setValue(this.marketingBannerData?.buttonLocationY);
    this.buttonPositionMobileControl.setValue(this.marketingBannerData?.buttonPositionMobile);
    this.buttonAlignmentMobileControl.setValue(this.marketingBannerData?.buttonAlignmentMobile ?? TextAlignment.Center);
    this.buttonLocationXMobileControl.setValue(this.marketingBannerData?.buttonLocationXMobile);
    this.buttonLocationYMobileControl.setValue(this.marketingBannerData?.buttonLocationYMobile);

    this.mobileMediaStyleControl.setValue(this.marketingBannerData?.mobileMediaStyle || 'slide');
    this.layoutStyleControl.setValue(this.marketingBannerData?.layoutStyle || 'normal');
    this.autoSlideDelayControl.setValue(this.marketingBannerData?.autoSlideDelay || 3.0);
  }

  private toTextValueJson(raw: any): string {
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && parsed.i18n) {
          return JSON.stringify(parsed);
        }
      } catch {
        // treat as plain text fallback
      }
    }
    if (raw && typeof raw === 'object' && raw.i18n) return JSON.stringify(raw);
    return JSON.stringify({ format: 'plain', i18n: { [this.activeLanguageCode]: raw ?? '' } });
  }

  private parseTextValue(raw: string | null | undefined): any {
    if (!raw) return { format: 'plain', i18n: {} };
    try { return JSON.parse(raw); } catch { return { format: 'plain', i18n: {} }; }
  }

  private loadAssetsByIds(ids: ID[]): Observable<Asset[]> {
    if (!ids || ids.length === 0) {
      return of([]);
    }
    return forkJoin(
      ids.map((id) =>
        this.dataService.product.getAsset(id.toString()).mapSingle((data) => data.asset)
      )
    ) as Observable<Asset[]>;
  }

  private areArraysEqual(array1: ID[], array2: ID[]): boolean {
    return (
      array1?.length === array2?.length && array1?.every((value) => array2?.includes(value))
    );
  }

  private attachMediasInput(inputComponent: DynamicFormInputComponent) {
    const seed = Array.isArray(this.mediasControl.value) ? this.mediasControl.value : [];
    inputComponent.writeValue(seed);
    inputComponent.registerOnChange((assets: Asset[]) => this.onMediasChange(assets));
  }

  private attachMobileMediasInput(inputComponent: DynamicFormInputComponent) {
    const seed = Array.isArray(this.mobileMediasControl.value) ? this.mobileMediasControl.value : [];
    inputComponent.writeValue(seed);
    inputComponent.registerOnChange((assets: Asset[]) => this.onMobileMediasChange(assets));
  }

  private onMediasChange = (assets: Asset[]) => {
    const assetIds = assets?.map((asset) => asset.id);
    if (!this.areArraysEqual(this.marketingBannerData?.mediaIds || [], assetIds)) {
      this.marketingBannerData!.mediaIds = assetIds;
      this.updateJsonString();
    }
  };

  private onMobileMediasChange = (assets: Asset[]) => {
    const assetIds = assets?.map((asset) => asset.id);
    if (!this.areArraysEqual(this.marketingBannerData?.mobileMediaIds || [], assetIds)) {
      this.marketingBannerData!.mobileMediaIds = assetIds;
      this.updateJsonString();
    }
  };

  private onBackgroundMediaChange = (asset: Asset | null) => {
    const assetId = asset ? asset.id : null;
    if (this.marketingBannerData?.backgroundMediaId !== assetId) {
      this.marketingBannerData!.backgroundMediaId = assetId;
      this.updateJsonString();
    }
  };

  private onBackgroundMobileMediaChange = (asset: Asset | null) => {
    const assetId = asset ? asset.id : null;
    if (this.marketingBannerData?.backgroundMobileMediaId !== assetId) {
      this.marketingBannerData!.backgroundMobileMediaId = assetId;
      this.updateJsonString();
    }
  };

  private onTypeChange = (value: MarketingBannerType) => {
    if (this.marketingBannerData?.type !== value) {
      this.marketingBannerData!.type = value;

      // Optionally set defaults for alignment/button if type changes
      if (
        value === MarketingBannerType.LeftMediaRightText ||
        value === MarketingBannerType.LeftTextRightMedia
      ) {
        this.marketingBannerData!.textAlignment = TextAlignment.Left;
        this.textAlignmentControl.setValue(TextAlignment.Left);
        this.marketingBannerData!.buttonPosition = ButtonPositionType.BelowText;
        this.buttonPositionControl.setValue(ButtonPositionType.BelowText);
        this.marketingBannerData!.buttonPositionMobile = ButtonPositionType.BelowText;
        this.buttonPositionMobileControl.setValue(ButtonPositionType.BelowText);
      } else if (
        value === MarketingBannerType.TopTextBelowMedia ||
        value === MarketingBannerType.TitledMedia
      ) {
        this.marketingBannerData!.textAlignment = TextAlignment.Center;
        this.textAlignmentControl.setValue(TextAlignment.Center);
        this.marketingBannerData!.buttonPosition = ButtonPositionType.Custom;
        this.buttonPositionControl.setValue(ButtonPositionType.Custom);
        this.marketingBannerData!.buttonPositionMobile = ButtonPositionType.Custom;
        this.buttonPositionMobileControl.setValue(ButtonPositionType.Custom);
      } else if (value === MarketingBannerType.TextOnly || value === MarketingBannerType.DualText) {
        this.marketingBannerData!.textAlignment = TextAlignment.Center;
        this.textAlignmentControl.setValue(TextAlignment.Center);
      }

      if (value === MarketingBannerType.MiddleMediaSidesText) {
        this.partRatioLabel = 'Middle Media Part Ratio';
      } else {
        this.partRatioLabel = 'Left Right Part Ratio';
      }

      this.updateJsonString();
    }
  };

  private onMediaHeightChange = (value: number) => {
    if (this.marketingBannerData?.mediaHeight !== value) {
      this.marketingBannerData!.mediaHeight = value;
      this.updateJsonString();
    }
  };

  private onMediaRoundedCornersChange = (value: string) => {
    if (this.marketingBannerData?.mediaRoundedCorners !== value) {
      this.marketingBannerData!.mediaRoundedCorners = value;
      this.updateJsonString();
    }
  };

  private onTitleChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.marketingBannerData?.title) !== JSON.stringify(next)) {
      this.marketingBannerData!.title = next;
      this.updateJsonString();
    }
  };

  private onTextChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.marketingBannerData?.text) !== JSON.stringify(next)) {
      this.marketingBannerData!.text = next;
      this.updateJsonString();
    }
  };

  private onTitleFontSizeChange = (value: number) => {
    if (this.marketingBannerData?.titleFontSize !== value) {
      this.marketingBannerData!.titleFontSize = value;
      this.updateJsonString();
    }
  };

  private onTextFontSizeChange = (value: number) => {
    if (this.marketingBannerData?.textFontSize !== value) {
      this.marketingBannerData!.textFontSize = value;
      this.updateJsonString();
    }
  };

  private onTextBarStyleChange = (value: string) => {
    if (this.marketingBannerData?.textBarStyle !== value) {
      this.marketingBannerData!.textBarStyle = value;
      this.updateJsonString();
    }
  };

  private onTextAlignmentChange = (value: TextAlignment) => {
    if (this.marketingBannerData?.textAlignment !== value) {
      this.marketingBannerData!.textAlignment = value;
      this.updateJsonString();
    }
  };

  private onLeftRightPartRatioChange = (value: number) => {
    if (this.marketingBannerData?.leftRightPartRatio !== value) {
      this.marketingBannerData!.leftRightPartRatio = value;
      this.updateJsonString();
    }
  };

  private onMediaTextGapChange = (value: number) => {
    if (this.marketingBannerData?.mediaTextGap !== value) {
      this.marketingBannerData!.mediaTextGap = value;
      this.updateJsonString();
    }
  };

  private onOverlayOpacityChange = (value: number) => {
    const shadowTransparency = 1.0 - clampUnitInterval(value);
    if (this.marketingBannerData?.shadowTransparency !== shadowTransparency) {
      this.marketingBannerData!.shadowTransparency = shadowTransparency;
      this.updateJsonString();
    }
  };

  private onBackgroundColorChange = (value: string) => {
    if (this.marketingBannerData?.backgroundColor !== value) {
      this.marketingBannerData!.backgroundColor = value;
      this.updateJsonString();
    }
  };

  private onTextColorChange = (value: string) => {
    if (this.marketingBannerData?.textColor !== value) {
      this.marketingBannerData!.textColor = value;
      this.updateJsonString();
    }
  };

  private onTextWidthChange = (value: number) => {
    if (this.marketingBannerData?.textWidth !== value) {
      this.marketingBannerData!.textWidth = value;
      this.updateJsonString();
    }
  };

  private onTitleIndentationChange = (value: number) => {
    if (this.marketingBannerData?.titleIndentation !== value) {
      this.marketingBannerData!.titleIndentation = value;
      this.updateJsonString();
    }
  };

  private onTextIndentationChange = (value: number) => {
    if (this.marketingBannerData?.textIndentation !== value) {
      this.marketingBannerData!.textIndentation = value;
      this.updateJsonString();
    }
  };

  private onSecColTitleChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.marketingBannerData?.secColTitle) !== JSON.stringify(next)) {
      this.marketingBannerData!.secColTitle = next;
      this.updateJsonString();
    }
  };

  private onSecColTextChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.marketingBannerData?.secColText) !== JSON.stringify(next)) {
      this.marketingBannerData!.secColText = next;
      this.updateJsonString();
    }
  };

  private onSecColTitleFontSizeChange = (value: number) => {
    if (this.marketingBannerData?.secColTitleFontSize !== value) {
      this.marketingBannerData!.secColTitleFontSize = value;
      this.updateJsonString();
    }
  };

  private onSecColTextFontSizeChange = (value: number) => {
    if (this.marketingBannerData?.secColTextFontSize !== value) {
      this.marketingBannerData!.secColTextFontSize = value;
      this.updateJsonString();
    }
  };

  private onSecColTextAlignmentChange = (value: TextAlignment) => {
    if (this.marketingBannerData?.secColTextAlignment !== value) {
      this.marketingBannerData!.secColTextAlignment = value;
      this.updateJsonString();
    }
  };

  private onSecColTextWidthChange = (value: number) => {
    if (this.marketingBannerData?.secColTextWidth !== value) {
      this.marketingBannerData!.secColTextWidth = value;
      this.updateJsonString();
    }
  };

  private onSecColTitleIndentationChange = (value: number) => {
    if (this.marketingBannerData?.secColTitleIndentation !== value) {
      this.marketingBannerData!.secColTitleIndentation = value;
      this.updateJsonString();
    }
  };

  private onSecColTextIndentationChange = (value: number) => {
    if (this.marketingBannerData?.secColTextIndentation !== value) {
      this.marketingBannerData!.secColTextIndentation = value;
      this.updateJsonString();
    }
  };

  private onButtonChange = (value: string) => {
    if (this.marketingBannerData && JSON.stringify(this.marketingBannerData.button) !== value) {
      this.marketingBannerData.button = safeJSONParse<BannerButtonData>(
        value,
        this.notificationService,
        defaultBannerButtonData
      );
      this.updateJsonString();
    }
  };

  private onButtonPositionChange = (value: ButtonPositionType) => {
    if (this.marketingBannerData?.buttonPosition !== value) {
      this.marketingBannerData!.buttonPosition = value;
      this.updateJsonString();
    }
  };

  private onButtonAlignmentChange = (value: TextAlignment) => {
    if (this.marketingBannerData?.buttonAlignment !== value) {
      this.marketingBannerData!.buttonAlignment = value;
      this.updateJsonString();
    }
  };

  private onButtonLocationXChange = (value: number) => {
    if (this.marketingBannerData?.buttonLocationX !== value) {
      this.marketingBannerData!.buttonLocationX = value;
      this.updateJsonString();
    }
  };

  private onButtonLocationYChange = (value: number) => {
    if (this.marketingBannerData?.buttonLocationY !== value) {
      this.marketingBannerData!.buttonLocationY = value;
      this.updateJsonString();
    }
  };

  private onButtonPositionMobileChange = (value: ButtonPositionType) => {
    if (this.marketingBannerData?.buttonPositionMobile !== value) {
      this.marketingBannerData!.buttonPositionMobile = value;
      this.updateJsonString();
    }
  };

  private onButtonAlignmentMobileChange = (value: TextAlignment) => {
    if (this.marketingBannerData?.buttonAlignmentMobile !== value) {
      this.marketingBannerData!.buttonAlignmentMobile = value;
      this.updateJsonString();
    }
  };

  private onButtonLocationXMobileChange = (value: number) => {
    if (this.marketingBannerData?.buttonLocationXMobile !== value) {
      this.marketingBannerData!.buttonLocationXMobile = value;
      this.updateJsonString();
    }
  };

  private onButtonLocationYMobileChange = (value: number) => {
    if (this.marketingBannerData?.buttonLocationYMobile !== value) {
      this.marketingBannerData!.buttonLocationYMobile = value;
      this.updateJsonString();
    }
  };

  private onMobileMediaPositionChange = (value: MobileMediaPosition) => {
    if (this.marketingBannerData?.mobileMediaPosition !== value) {
      this.marketingBannerData!.mobileMediaPosition = value;
      this.updateJsonString();
    }
  };

  private onMobileMediaStyleChange = (value: string) => {
    if (this.marketingBannerData?.mobileMediaStyle !== value) {
      this.marketingBannerData!.mobileMediaStyle = value;
      this.updateJsonString();
    }
  };

  private onLayoutStyleChange = (value: string) => {
    if (this.marketingBannerData?.layoutStyle !== value) {
      this.marketingBannerData!.layoutStyle = value;
      this.updateJsonString();
    }
  };

  private onautoSlideDelayChange = (value: number) => {
    if (this.marketingBannerData?.autoSlideDelay !== value) {
      this.marketingBannerData!.autoSlideDelay = value;
      this.updateJsonString();
    }
  };

  private updateJsonString() {
    const jsonString = JSON.stringify(this.marketingBannerData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
